import { Button, ControlGroup, HTMLSelect, IHTMLSelectProps, IOptionProps, FormGroup, Colors } from '@blueprintjs/core'
import * as React from 'react'
import useAPI from '../../hooks/useAPI'

export interface APISelectProps extends IHTMLSelectProps {
  request: () => Promise<any>
  process: (any) => IOptionProps[]
  blankOption?: IOptionProps
  onReceiveData?: (any) => void
}

const renderError = error => error && <span style={{ color: Colors.RED2 }}>{error}</span>

// Creates a select dropdown by wrapping a native select to avoid the nastiness of pure-react select components.
// Downside: it's not searchable like LoadableSelect
export const APISelect: React.FC<APISelectProps> = ({
  request,
  process,
  blankOption,
  onReceiveData,
  ...selectProps
}) => {
  // on load
  const [{ loading, data, error }, refresh] = useAPI(request)
  React.useEffect(() => {
    refresh()
    // Regardless of whether consuming code properly memoizes "request", we still only want to call this when the component is created
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (onReceiveData) onReceiveData(data)
    // including onReceiveData breaks this component. Clicking another value won't change what's selected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const options = React.useMemo(() => {
    if (!data) return []
    return blankOption ? [blankOption, ...process(data)] : process(data)
  }, [data, blankOption, process])

  return (
    <FormGroup helperText={error && renderError('Something went wrong. Please try again.')}>
      <ControlGroup fill>
        <HTMLSelect {...selectProps} disabled={loading || selectProps.disabled}>
          {loading && <option>Loading...</option>}
          {!loading && (
            <>
              {options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </>
          )}
        </HTMLSelect>
        {loading && <Button loading={loading} />}
      </ControlGroup>
    </FormGroup>
  )
}
